import { Box, Chip, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import compInfos from "./broadcasting.json";
import { useLangContext } from "../../../context/langContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { routerPaths } from "../../../constants/Paths";
import { formatDateWithTime } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";
import {
  BroadCastingSendSpeedEnum,
  BroadCastingStatusEnum,
} from "../../../services/Schemas/Broadcast";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import { useUrl } from "../../../hooks/urlEncoding";
import { BroadcastingListRequest } from "../../../services/Endpoints/broadcast";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";
import {
  changeToToman,
  formatCurrency,
} from "../../../utils/currency-formater";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  CallCalling,
  CallOutgoing,
  CallReceived,
  DocumentText,
  Edit,
  MoneySend,
} from "iconsax-react";
import {
  NumberListColumnFilterable,
  NumberListColumnSortable,
} from "../../../services/Endpoints/broadcast/NumbersEndpoint";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import {
  CallDetailListColumnFilterable,
  CallDetailListColumnSortable,
} from "../../../services/Endpoints/broadcast/PbxCallDetails";
import ViewActionLink from "../../common/view-action/ViewActionLink";
import useTableSchema from "../../../hooks/useTableSchema";
import { ITableSchema } from "../../../utils/types";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../hooks/useUrlSort";
import {
  WalletTransactionListColumnFilterable,
  WalletTransactionListColumnSortable,
} from "../../../services/Endpoints/wallet/WalletTransactionListEndpoint";
import {
  LogListColumnFilterable,
  LogListColumnSortable,
} from "../../../services/Endpoints/logs/LogListEndpoints";
import MuiBaseModal from "../../common/modal/MuiModal";
import BroadcastingPickupEditForm from "./BroadcastingPickupEditForm";
import {
  SendCallsListColumnFilterable,
  SendCallsListColumnSortable,
} from "../../../services/Endpoints/send-calls/SendCallsEndpoint";
interface IBroadcast {
  status: string | ReactNode;
  id: number;
  caption: string| ReactNode;
  contacts: number;
  created_at: string;
  operations: ReactNode;
  send_speed: string | ReactNode;
}

const statusOptions = {
  [BroadCastingStatusEnum.PENDING]: "در انتظار پرداخت",
  [BroadCastingStatusEnum.PAYING]: "در حال پرداخت",
  [BroadCastingStatusEnum.PAID]: "پرداخت شده",
  [BroadCastingStatusEnum.SENDING]: "در حال ارسال",
  [BroadCastingStatusEnum.DONE]: "تمام شده",
  [BroadCastingStatusEnum.SENT]: "ارسال شده",
  [BroadCastingStatusEnum.CANCELED]: "کنسل شده",
};

const sendSpeedOptions = {
  [BroadCastingSendSpeedEnum.FAST]: "سریع",
  [BroadCastingSendSpeedEnum.NORMAL]: "عادی",
};

export default function BroadcastingTabsComponent() {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value] = useState("1");
  const { lang, dir } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [data, setData] = useState<IBroadcast[] | []>([]);
  const [waiting, setWaiting] = useState(false);
  const [reload, setReload] = useState(false);
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const protectedInstance = useProtectedAxiosInstance();
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState<{
    id: number | null;
    pickupCount: number;
    pickupLoopCounter: number;
  }>({ id: null, pickupCount: 0, pickupLoopCounter: 0 });

  const handleOpenModal = (
    pickupCount: number,
    pickupLoopCounter: number,
    id: number,
  ) => {
    setEditData({ pickupCount, pickupLoopCounter, id });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const getData = async () => {
    try {
      const broadcastingListRequest = new BroadcastingListRequest(
        protectedInstance,
      );
      setWaiting(true);
      const resp = await broadcastingListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      const broadcast = resp.data.voice_broadcasts.map(
        ({
          caption,
          id,
          status,
          created_at,
          total_numbers,
          estimated_cost,
          file_duration,
          retry_count,
          estimated_duration,
          pbx_trunk,
          actual_cost,
          pickup_loop_counter,
          pickup_count,
          send_speed,
        }) => ({
          estimated_cost,
          file_duration,
          retry_count,
          estimated_duration,
          actual_cost,
          caption: pbx_trunk ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <span>{caption}</span>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Chip
                  color="primary"
                  size="small"
                  label={`ارسال کننده :  ${pbx_trunk.caption} `}
                />
              </div>
            </div>
          ) : (
            caption
          ),
          created_at,
          contacts: total_numbers,
          status: (
            <InfoBadge
              origin={OriginEnum.BROADCASTING_STATUS}
              status={status as "pending"}
            />
          ),
          send_speed: (
            <InfoBadge
              origin={OriginEnum.BROADCASTING_SPEED}
              status={send_speed as "normal"}
            />
          ),
          id,
          operations: (
            <Stack direction="row" gap="3px" justifyContent="center">
              <ViewActionLink
                disabled={!can(AccessEnums.AdminPbxCallDetailRecordsList)}
                link={`/${routerPaths.VOICE_BROADCASTS}/${id}`}
              />
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminVoiceBroadcastsUpdate)}
                title="ویرایش برداشت ها"
              >
                <Stack
                  direction="row"
                  justifyContent="center"
                  gap="4px"
                  onClick={() =>
                    handleOpenModal(pickup_count, pickup_loop_counter, id)
                  }
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                    },
                  }}
                >
                  <Edit size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminSendCallsList)}
                title="درخواست تماس"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.SEND_CALLS, {
                    filters: {
                      [SendCallsListColumnFilterable.VOICE_BROADCAST_ID]: {
                        column:
                          SendCallsListColumnFilterable.VOICE_BROADCAST_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [SendCallsListColumnSortable.ID]: {
                        column: SendCallsListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <CallReceived size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminVoiceBroadcastNumbersList)}
                title="شماره ها"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.BROAD_NUMBER, {
                    filters: {
                      [NumberListColumnFilterable.VOICE_BROADCAST_ID]: {
                        column: NumberListColumnFilterable.VOICE_BROADCAST_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [NumberListColumnSortable.ID]: {
                        column: NumberListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <CallOutgoing size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminPbxCallDetailRecordsList)}
                title="تماس ها"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.PBX_CALL_DETAIL, {
                    filters: {
                      [CallDetailListColumnFilterable.VOICE_BROADCAST_ID]: {
                        column:
                          CallDetailListColumnFilterable.VOICE_BROADCAST_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [CallDetailListColumnSortable.ID]: {
                        column: CallDetailListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <CallCalling size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminTransactionsList)}
                title="تراکنش های کمپین"
              >
                <Stack
                  direction="row"
                  justifyContent="center"
                  gap="4px"
                  component={Link}
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                    },
                  }}
                  to={queryStringUrl(routerPaths.WALLET_TRANSACTION, {
                    filters: {
                      [WalletTransactionListColumnFilterable.VOICE_BROADCAST_ID]:
                        {
                          operator: FilterOperatorEnum.EQUAL,
                          column:
                            WalletTransactionListColumnFilterable.VOICE_BROADCAST_ID,
                          value: id,
                        },
                    },
                    sorts: {
                      [WalletTransactionListColumnSortable.ID]: {
                        column: WalletTransactionListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <MoneySend size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.MODEL]: {
                        column: LogListColumnFilterable.MODEL,
                        operator: FilterOperatorEnum.EQUAL,
                        value: "voice_broadcasts",
                      },
                      [LogListColumnFilterable.REFERENCE_MODEL]: {
                        column: LogListColumnFilterable.REFERENCE_MODEL,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        }),
      );
      setWaiting(false);
      setData(broadcast);
      setTotal(resp.data.total);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [reload, page, value, rowsPerPage, location.search]);

  const tInfo = compInfos[lang];
  const colName = tInfo.columns;

  const broadcastColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "caption",
      label: "نام کمپین",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "contacts",
      label: " مخاطبین",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "retry_count",
      label: " تکرار",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "send_speed",
      label: " سرعت ارسال",
      minWidth: 130,
      align: "center",
      FilterComponent: SelectionFilter,
      options: sendSpeedOptions,
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 100,
      align: "center",
      format: (value: string) =>
        value && formatDateWithTime({ lang, date: value }),
      FilterComponent: DateTimeFilter,
    },
    {
      key: "file_duration",
      label: "طول فایل",
      minWidth: 170,
      align: "center",
      format: (value) => `${value} ثانیه`,
      FilterComponent: NumberFilter,
    },
    {
      key: "estimated_duration",
      label: "زمان مکالمه تقریبی",
      minWidth: 170,
      align: "center",
      format: (value) => `${value} ثانیه`,
      FilterComponent: NumberFilter,
    },
    {
      key: "estimated_cost",
      label: "هزینه تقریبی",
      minWidth: 100,
      align: "center",
      format: (value) => `${formatCurrency(changeToToman(value))} تومان`,
      FilterComponent: NumberFilter,
    },
    {
      key: "actual_cost",
      label: "هزینه",
      minWidth: 100,
      align: "center",
      format: (value) => `${formatCurrency(changeToToman(value))} تومان`,
      FilterComponent: NumberFilter,
    },
    {
      key: "status",
      label: "وضعیت",
      minWidth: 170,
      align: "center",
      FilterComponent: SelectionFilter,
      options: statusOptions,
    },
    {
      key: "operations",
      label: colName.operations,
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(broadcastColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setReload(!reload)}
        hasWaiting={waiting}
        hasTopPagination
        columns={columns}
        hasSearchFilter
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={data}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />

      <MuiBaseModal
        open={openModal}
        onClose={handleCloseModal}
        title="ویرایش برداشت"
        hasCloseIcon
      >
        <BroadcastingPickupEditForm
          id={editData.id}
          pickupCount={editData.pickupCount}
          pickupLoopCounter={editData.pickupLoopCounter}
          onClose={handleCloseModal}
          onSuccess={getData}
        />
      </MuiBaseModal>
    </Box>
  );
}
